import { CookingPot, Droplets, Egg, Flame, Heart, Milk, Microwave, Sparkles, Wind } from "lucide-react";
import Reveal from "@/components/Reveal";

const NEED = [
  { icon: Egg, label: "3 eggs" },
  { icon: Droplets, label: "75 ml butter, ghee or oil" },
  { icon: Sparkles, label: "1 tsp vanilla essence" },
  { icon: Milk, label: "Splash of milk or water, if needed" },
];

const METHODS = [
  {
    id: "oven",
    icon: Flame,
    title: "Oven",
    steps: [
      "Preheat oven to 170°C (340°F). Grease a 6-inch round pan.",
      "Pour batter into the pan.",
      "Bake for 25–30 mins or until a toothpick comes out clean.",
    ],
  },
  {
    id: "cooker",
    icon: CookingPot,
    title: "Pressure Cooker",
    steps: [
      "Preheat cooker on medium heat for 5 mins. Remove weight / whistle.",
      "Place the pan on a stand. Close lid.",
      "Cook on low heat for 25–30 mins. Check with a toothpick.",
    ],
  },
  {
    id: "airfryer",
    icon: Wind,
    title: "Air Fryer",
    steps: [
      "Preheat air fryer to 160°C for 3 mins. Grease a cake tin that fits your basket.",
      "Pour batter into the tin.",
      "Air fry for 18–22 mins. Check with a toothpick.",
      "Browning too fast? Cover loosely with foil.",
    ],
  },
  {
    id: "microwave",
    icon: Microwave,
    title: "Microwave",
    steps: [
      "Grease a microwave-safe bowl or pan.",
      "Pour batter.",
      "Microwave on medium power for 5–7 mins.",
      "Cooking time may vary by microwave.",
    ],
  },
];

export default function HowToBake() {
  return (
    <section id="how-to-bake" className="py-24 sm:py-32 relative overflow-hidden">
      <div className="absolute top-20 -right-32 w-[420px] h-[420px] rounded-full bg-[#D1E8E2]/50 blur-[110px]" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
        <Reveal className="mb-12 sm:mb-16 text-center max-w-2xl mx-auto">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#C86D51] mb-4">How to Bake</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#2C221E]">
            One bowl. Five minutes.<br /><em className="italic text-[#6A2C3E]">Any</em> kitchen.
          </h2>
          <p className="mt-5 text-[#6E5E57] leading-relaxed">
            Straight from the back of the box — oven, pressure cooker, air fryer or microwave, every Almo
            mix bakes up moist and tender.
          </p>
        </Reveal>

        <Reveal className="mb-12">
          <div className="rounded-[2rem] border border-[#2C221E]/12 bg-[#FFF3D1]/60 px-7 sm:px-10 py-8" data-testid="you-will-need">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#6A2C3E] text-center mb-6">
              You will need
            </p>
            <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
              {NEED.map(({ icon: Icon, label }) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-2.5 rounded-full bg-[#FAF7F2] border border-[#2C221E]/12 px-5 py-3 text-sm font-medium text-[#2C221E] shadow-sm"
                >
                  <Icon className="w-4 h-4 text-[#C86D51]" /> {label}
                </span>
              ))}
            </div>
          </div>
        </Reveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="baking-methods">
          {METHODS.map((m, i) => (
            <Reveal key={m.id} delay={i * 0.1}>
              <div
                data-testid={`method-card-${m.id}`}
                className="h-full rounded-[2rem] bg-[#FAF7F2] border border-[#2C221E]/10 p-7 sm:p-9 hover:border-[#2C221E]/25 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-[#2C221E]/10 transition-all duration-300"
              >
                <div className="flex items-center gap-4">
                  <span className="w-12 h-12 rounded-2xl bg-[#2C221E] text-[#FAF7F2] flex items-center justify-center">
                    <m.icon className="w-5 h-5" />
                  </span>
                  <h3 className="font-serif text-2xl font-semibold text-[#2C221E]">{m.title}</h3>
                </div>
                <ol className="mt-7 space-y-4">
                  {m.steps.map((s, j) => (
                    <li key={j} className="flex gap-3.5 text-sm text-[#2C221E]/80 leading-relaxed">
                      <span className="font-mono text-xs text-[#C86D51] mt-0.5 shrink-0">
                        {String(j + 1).padStart(2, "0")}
                      </span>
                      {s}
                    </li>
                  ))}
                </ol>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-10 text-center">
          <p className="inline-flex items-center gap-2.5 font-serif italic text-lg sm:text-xl text-[#6A2C3E]">
            Cool completely? Not a chance — enjoy it warm, straight from the oven!
            <Heart className="w-4 h-4 text-[#C86D51] fill-current not-italic" />
          </p>
        </Reveal>
      </div>
    </section>
  );
}
